import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { Clock, CheckCircle, XCircle, AlertCircle, Filter, Calendar, User } from "react-feather";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  listStaffOvertimeSummaryApi,
  listStaffOvertimeRequestsApi,
  approveOvertimeRequestApi,
} from "../../../Api/overtimePolicyApi";
import { attendanceMusterRollReport } from "../../../Api/HrAttendance";
import {
  checkStatusCodeSuccess,
  getLocalItem,
  extractYearWiseDate,
} from "../../../Constant/common";
import BaseModal from "../../../BaseComponents/BaseModal";
import BaseTooltip from "../../../BaseComponents/BaseTooltip";
import { centerApi } from "../../../Api/common";
import { BaseStaffURL } from "../../../Api/Service";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_FILTERS = ["All", "Pending", "Approved", "Rejected"];

const STATUS_CONFIG = {
  Pending: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.25)",
    icon: AlertCircle,
  },
  Approved: {
    color: "#10b981",
    bg: "rgba(16,185,129,0.12)",
    border: "rgba(16,185,129,0.25)",
    icon: CheckCircle,
  },
  Rejected: {
    color: "#f43f5e",
    bg: "rgba(244,63,94,0.12)",
    border: "rgba(244,63,94,0.25)",
    icon: XCircle,
  },
};

// Real-time backend API integrated. Mock/dummy data has been removed.

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtHours = (mins) => {
  if (mins == null) return "—";
  const h = Math.floor(Number(mins) / 60);
  const m = Number(mins) % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Pending;
  const Icon = cfg.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 700,
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <Icon size={11} strokeWidth={2.5} />
      {status}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
const StatCard = ({ label, count, color, bg, border, icon: Icon }) => (
  <div
    style={{
      flex: "1 1 120px",
      padding: "14px 16px",
      borderRadius: 12,
      background: bg,
      border: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      gap: 12,
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: `${color}20`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={16} strokeWidth={2.5} style={{ color }} />
    </div>
    <div>
      <p
        style={{
          fontSize: "0.68rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--text-muted)",
          margin: 0,
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: "1.25rem", fontWeight: 800, color, margin: "2px 0 0" }}>{count}</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Staff Overtime Dashboard Component (Detail Pane)
// ---------------------------------------------------------------------------
const StaffOvertimeDashboard = ({
  group,
  onApprove,
  onReject,
  autoOt = true,
  monthlyOvertimeHours = 0,
}) => {
  const { staff, requests } = group;
  const staffName = staff?.name ?? "—";
  const staffId = staff?.staff_id ?? "";
  const designation = staff?.designation ?? "";
  const initials =
    staffName
      .split(" ")
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

  // Calculate statistics
  const getMinutes = (r) => {
    if (r?.status === "Approved") {
      return (
        r?.approved_minutes ??
        r?.approvedMinutes ??
        r?.requested_minutes ??
        r?.requestedMinutes ??
        r?.overtime_minutes ??
        (r?.overtime_hours != null ? r.overtime_hours * 60 : 0)
      );
    }
    return (
      r?.requested_minutes ??
      r?.requestedMinutes ??
      r?.overtime_minutes ??
      (r?.overtime_hours != null ? r.overtime_hours * 60 : 0)
    );
  };

  const totalMinutes = requests.reduce((acc, r) => acc + getMinutes(r), 0);
  const pendingList = requests.filter((r) => r?.status === "Pending");
  const approvedList = requests.filter((r) => r?.status === "Approved");
  const rejectedList = requests.filter((r) => r?.status === "Rejected");

  const pendingMinutes = pendingList.reduce((acc, r) => acc + getMinutes(r), 0);
  const approvedMinutes = approvedList.reduce((acc, r) => acc + getMinutes(r), 0);
  const rejectedMinutes = rejectedList.reduce((acc, r) => acc + getMinutes(r), 0);

  const historyList = requests.filter((r) => r?.status !== "Pending");

  return (
    <div
      className="glass rounded-2xl animate-fade-in"
      style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}
    >
      {/* Profile Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          borderBottom: "1px solid var(--glass-border)",
          paddingBottom: 14,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1rem",
            fontWeight: 800,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <h2
            style={{
              fontSize: "1.05rem",
              fontWeight: 800,
              color: "var(--text-primary)",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {staffName}
          </h2>
          <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "3px 0 0" }}>
            ID: <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>{staffId}</span>
            {designation && (
              <>
                {" "}
                ·{" "}
                <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>
                  {designation}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Staff stats rows */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {/* Pending OT */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderRadius: 12,
            background: "rgba(245,158,11,0.06)",
            border: "1px solid rgba(245,158,11,0.15)",
          }}
        >
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: "#f59e0b",
              letterSpacing: "0.02em",
            }}
          >
            Pending OT
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                color: "#f59e0b",
                fontWeight: 600,
                background: "rgba(245,158,11,0.12)",
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              {pendingList.length} pending
            </span>
            <span style={{ fontSize: "0.9375rem", fontWeight: 800, color: "#f59e0b" }}>
              {fmtHours(pendingMinutes)}
            </span>
          </div>
        </div>

        {/* Approved OT */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderRadius: 12,
            background: "rgba(16,185,129,0.06)",
            border: "1px solid rgba(16,185,129,0.15)",
          }}
        >
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: "#10b981",
              letterSpacing: "0.02em",
            }}
          >
            Approved OT
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                color: "#10b981",
                fontWeight: 600,
                background: "rgba(16,185,129,0.12)",
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              {approvedList.length} approved
            </span>
            <span style={{ fontSize: "0.9375rem", fontWeight: 800, color: "#10b981" }}>
              {fmtHours(approvedMinutes)}
            </span>
          </div>
        </div>

        {/* Rejected OT */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderRadius: 12,
            background: "rgba(244,63,94,0.06)",
            border: "1px solid rgba(244,63,94,0.15)",
          }}
        >
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: "#f43f5e",
              letterSpacing: "0.02em",
            }}
          >
            Rejected OT
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                color: "#f43f5e",
                fontWeight: 600,
                background: "rgba(244,63,94,0.12)",
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              {rejectedList.length} rejected
            </span>
            <span style={{ fontSize: "0.9375rem", fontWeight: 800, color: "#f43f5e" }}>
              {fmtHours(rejectedMinutes)}
            </span>
          </div>
        </div>

        {/* Total Logged */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderRadius: 12,
            background: "rgba(99,102,241,0.06)",
            border: "1px solid rgba(99,102,241,0.15)",
          }}
        >
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: "#6366f1",
              letterSpacing: "0.02em",
            }}
          >
            Total Logged
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                color: "#6366f1",
                fontWeight: 600,
                background: "rgba(99,102,241,0.12)",
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              {requests.length} logs total
            </span>
            <span style={{ fontSize: "0.9375rem", fontWeight: 800, color: "#6366f1" }}>
              {fmtHours(totalMinutes)}
            </span>
          </div>
        </div>

        {/* Monthly OT */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderRadius: 12,
            background: "rgba(6,182,212,0.06)",
            border: "1px solid rgba(6,182,212,0.15)",
          }}
        >
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: "#06b6d4",
              letterSpacing: "0.02em",
            }}
          >
            Monthly OT
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                color: "#06b6d4",
                fontWeight: 600,
                background: "rgba(6,182,212,0.12)",
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              This Month
            </span>
            <span style={{ fontSize: "0.9375rem", fontWeight: 800, color: "#06b6d4" }}>
              {fmtHours(monthlyOvertimeHours * 60)}
            </span>
          </div>
        </div>
      </div>

      {/* Pending Approvals Section (Directly visible, no scrolling) */}
      {pendingList.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h3
            style={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#f59e0b",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <AlertCircle size={14} strokeWidth={2.5} />
            Pending Approvals ({pendingList.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pendingList.map((req, rIdx) => {
              const date = req?.overtime_date ?? req?.date ?? "";
              const hours = getMinutes(req);
              const reason = req?.reason ?? "";
              const remarks = req?.remarks ?? "";

              return (
                <div
                  key={`pending-${req?.id ?? rIdx}`}
                  style={{
                    background: "rgba(245, 158, 11, 0.04)",
                    border: "1.5px dashed rgba(245, 158, 11, 0.25)",
                    borderRadius: 14,
                    padding: "14px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    boxShadow: "0 2px 10px -2px rgba(245, 158, 11, 0.06)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        style={{
                          fontSize: "0.8125rem",
                          fontWeight: 700,
                          color: "var(--text-primary)",
                        }}
                      >
                        {fmtDate(date)}
                      </span>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 800,
                          color: "#f59e0b",
                          background: "rgba(245,158,11,0.12)",
                          padding: "3px 9px",
                          borderRadius: 6,
                        }}
                      >
                        {fmtHours(hours)}
                      </span>
                    </div>
                    <StatusBadge status="Pending" />
                  </div>

                  {reason && (
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "0.8125rem",
                        color: "var(--text-secondary)",
                        lineHeight: "1.4",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "var(--text-muted)" }}>Reason:</span>{" "}
                      {reason}
                    </p>
                  )}

                  {remarks && (
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "0.8125rem",
                        color: "var(--text-muted)",
                        fontStyle: "italic",
                      }}
                    >
                      <span style={{ fontWeight: 600, fontStyle: "normal" }}>Remarks:</span>{" "}
                      {remarks}
                    </p>
                  )}

                  {autoOt && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                        marginTop: 4,
                        borderTop: "1px solid rgba(245, 158, 11, 0.12)",
                        paddingTop: 8,
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-cancel"
                        style={{
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          height: "auto",
                          border: "1px solid rgba(244, 63, 94, 0.3)",
                          color: "#f43f5e",
                          background: "rgba(244, 63, 94, 0.05)",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReject(req);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(244, 63, 94, 0.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(244, 63, 94, 0.05)";
                        }}
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          height: "auto",
                          background: "#10b981",
                          borderColor: "#10b981",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onApprove(req);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#059669";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#10b981";
                        }}
                      >
                        Approve
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History Log */}
      <div>
        <h3
          style={{
            fontSize: "0.8rem",
            fontWeight: 700,
            color: "var(--text-secondary)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            margin: "0 0 10px",
          }}
        >
          Overtime Logs & History
        </h3>

        {/* Requests sublist */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxHeight: 320,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          {historyList.length === 0 ? (
            <div
              style={{
                padding: "24px 20px",
                textAlign: "center",
                border: "1.5px dashed var(--glass-border)",
                borderRadius: 14,
                color: "var(--text-muted)",
                fontSize: "0.78rem",
              }}
            >
              No completed overtime history logs.
            </div>
          ) : (
            historyList.map((req, rIdx) => {
              const date = req?.overtime_date ?? req?.date ?? "";
              const hours = getMinutes(req);
              const reason = req?.reason ?? "";
              const remarks = req?.remarks ?? "";

              return (
                <div
                  key={req?.id ?? rIdx}
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: 12,
                    padding: "12px 14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        style={{
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                        }}
                      >
                        {fmtDate(date)}
                      </span>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "var(--brand-indigo)",
                          background: "var(--icon-btn-bg)",
                          padding: "2px 8px",
                          borderRadius: 6,
                        }}
                      >
                        {fmtHours(hours)}
                      </span>
                    </div>
                    <StatusBadge status={req?.status} />
                  </div>

                  {reason && (
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "0.78rem",
                        color: "var(--text-secondary)",
                        lineHeight: "1.4",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "var(--text-muted)" }}>Reason:</span>{" "}
                      {reason}
                    </p>
                  )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      marginTop: 4,
                      paddingTop: 4,
                      borderTop: "1px solid rgba(0,0,0,0.04)",
                    }}
                  >
                    {req?.request_type && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <span
                          style={{
                            fontSize: "0.65rem",
                            color: "var(--text-muted)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Request Type
                        </span>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                          }}
                        >
                          {req.request_type}
                        </span>
                      </div>
                    )}

                    {req?.calculation_type && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          textAlign: "right",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.65rem",
                            color: "var(--text-muted)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Calculation Type
                        </span>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                          }}
                        >
                          {req.calculation_type}
                        </span>
                      </div>
                    )}
                  </div>

                  {remarks && (
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "0.78rem",
                        color: "var(--text-muted)",
                        fontStyle: "italic",
                      }}
                    >
                      <span style={{ fontWeight: 600, fontStyle: "normal" }}>Remarks:</span>{" "}
                      {remarks}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const OvertimeRequests = () => {
  const [staffSummaries, setStaffSummaries] = useState([]);
  const [staffRequests, setStaffRequests] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [autoOt, setAutoOt] = useState(true);
  const [totalOvertimeHoursThisMonth, setTotalOvertimeHoursThisMonth] = useState(0);
  const [overtimePolicy, setOvertimePolicy] = useState(null);
  const searchInputRef = useRef(null);

  /* Force padding-left after mount — bypasses any !important CSS rule */
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.style.setProperty("padding-left", "34px", "important");
    }
  }, []);

  // Action modal state
  const [actionTarget, setActionTarget] = useState(null); // { request, type: "approve"|"reject" }
  const [actionRemarks, setActionRemarks] = useState("");
  const [actionLoader, setActionLoader] = useState(false);
  const [approvedMinutes, setApprovedMinutes] = useState("");
  const [attendanceData, setAttendanceData] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  useEffect(() => {
    document.title = "Overtime Requests — HR Fusions";
  }, []);

  // ── Fetch Staff Summaries ──────────────────────────────────────────────────
  const fetchStaffSummaries = useCallback(() => {
    setIsLoadingStaff(true);
    const orgId = getLocalItem("organizationId");
    const payload = {
      organization_id: Number(orgId),
      pageNumber: 1,
      pageSize: 1000,
    };
    listStaffOvertimeSummaryApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const list = resp?.data?.data ?? resp?.data ?? [];
          setStaffSummaries(Array.isArray(list) ? list : []);
        } else {
          setStaffSummaries([]);
        }
      })
      .catch(() => {
        setStaffSummaries([]);
      })
      .finally(() => setIsLoadingStaff(false));
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchStaffSummaries();
    });
  }, [fetchStaffSummaries]);

  // Fetch active overtime policy to check if auto_ot is disabled
  useEffect(() => {
    const orgId = getLocalItem("organizationId");
    if (!orgId) return;
    centerApi({ condition: { organizationId: Number(orgId) } })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const activePolicy = resp?.data?.activeOvertimePolicy;
          if (activePolicy) {
            setAutoOt(activePolicy.auto_ot !== false);
          }
        }
      })
      .catch(() => {});
  }, []);

  // ── Client-side filtering & search ─────────────────────────────────────────
  const filteredStaff = useMemo(() => {
    return staffSummaries.filter((staff) => {
      const name = (staff.employee_name ?? "").toLowerCase();
      const code = (staff.employee_code ?? "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || name.includes(q) || code.includes(q);

      let matchesStatus = true;
      if (statusFilter === "Pending") {
        matchesStatus = (staff.pending_requests_count ?? 0) > 0;
      } else if (statusFilter === "Approved") {
        matchesStatus = (staff.approved_requests_count ?? 0) > 0;
      } else if (statusFilter === "Rejected") {
        matchesStatus = (staff.rejected_requests_count ?? 0) > 0;
      }

      return matchesSearch && matchesStatus;
    });
  }, [staffSummaries, searchQuery, statusFilter]);

  // ── Active Selected ID and Staff Summary ──────────────────────────────────
  const effectiveSelectedId = selectedStaffId || filteredStaff[0]?.staff_id || "";

  const activeStaff = useMemo(() => {
    return filteredStaff.find((s) => s.staff_id === effectiveSelectedId) || null;
  }, [filteredStaff, effectiveSelectedId]);

  // ── Fetch Staff Requests ───────────────────────────────────────────────────
  const fetchStaffRequests = useCallback(
    (staffId) => {
      if (!staffId) return;
      setIsLoadingRequests(true);
      const orgId = getLocalItem("organizationId");
      const payload = {
        organization_id: Number(orgId),
        staff_id: Number(staffId),
        ...(statusFilter !== "All" ? { status: statusFilter } : {}),
        ...(dateFrom ? { fromDate: extractYearWiseDate(dateFrom) } : {}),
        ...(dateTo ? { toDate: extractYearWiseDate(dateTo) } : {}),
        pageNumber: 1,
        pageSize: 200,
      };
      listStaffOvertimeRequestsApi(payload)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const list = resp?.data?.data ?? [];
            setStaffRequests(Array.isArray(list) ? list : []);
            setTotalOvertimeHoursThisMonth(resp?.data?.total_overtime_hours_this_month ?? 0);
            setOvertimePolicy(resp?.data?.overtimePolicy ?? null);
          } else {
            setStaffRequests([]);
            setTotalOvertimeHoursThisMonth(0);
            setOvertimePolicy(null);
          }
        })
        .catch(() => {
          setStaffRequests([]);
          setTotalOvertimeHoursThisMonth(0);
          setOvertimePolicy(null);
        })
        .finally(() => setIsLoadingRequests(false));
    },
    [statusFilter, dateFrom, dateTo],
  );

  useEffect(() => {
    Promise.resolve().then(() => {
      if (effectiveSelectedId) {
        fetchStaffRequests(effectiveSelectedId);
      } else {
        setStaffRequests([]);
        setTotalOvertimeHoursThisMonth(0);
      }
    });
  }, [effectiveSelectedId, fetchStaffRequests]);

  // ── Stat counts (globally summed from summaries) ───────────────────────────
  const pendingCount = useMemo(
    () => staffSummaries.reduce((sum, s) => sum + (s.pending_requests_count ?? 0), 0),
    [staffSummaries],
  );
  const approvedCount = useMemo(
    () => staffSummaries.reduce((sum, s) => sum + (s.approved_requests_count ?? 0), 0),
    [staffSummaries],
  );
  const rejectedCount = useMemo(
    () => staffSummaries.reduce((sum, s) => sum + (s.rejected_requests_count ?? 0), 0),
    [staffSummaries],
  );
  const totalCount = useMemo(
    () => pendingCount + approvedCount + rejectedCount,
    [pendingCount, approvedCount, rejectedCount],
  );

  // ── Helper function to format time for display ──────────────────────────────────
  const formatTimeForDisplay = (timeStr) => {
    if (!timeStr) return "—";

    // Handle format like "12:00 hours" or "01:30 hours"
    if (timeStr.includes("hours")) {
      const timePart = timeStr.replace("hours", "").trim();
      const [hours, minutes] = timePart.split(":");
      return `${parseInt(hours)}h ${minutes}m`;
    }

    // Handle format like "0.50 hrs"
    if (timeStr.includes("hrs")) {
      const decimalHours = parseFloat(timeStr.replace("hrs", "").trim());
      const hours = Math.floor(decimalHours);
      const minutes = Math.round((decimalHours - hours) * 60);
      return `${hours}h ${minutes}m`;
    }

    return timeStr;
  };

  // ── Fetch attendance data for overtime modal ──────────────────────────────────
  const fetchAttendanceData = useCallback((requestDate, staffId) => {
    if (!requestDate || !staffId) return;

    setLoadingAttendance(true);
    const dateObj = new Date(requestDate);
    const searchDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;

    const payload = {
      searchDate: searchDate,
      staff_id: staffId,
      pageSize: null,
      pageNumber: 1,
      search: "",
      order: ["date", "desc"],
      date: requestDate,
    };

    attendanceMusterRollReport(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const transformedData = resp?.data?.transformedResponse?.[0];
          if (transformedData) {
            const attendanceRecord = transformedData.attendanceData?.[0];
            const overtimePolicy = resp?.data?.overtimePolicy;
            setAttendanceData({
              total_working_hours: attendanceRecord?.total_working_hours,
              applicable_over_time: attendanceRecord?.applicable_over_time,
              overtime_calculation_type: overtimePolicy?.calculation_type,
              minimum_ot_threshold: overtimePolicy?.minimum_ot_threshold,
            });
          } else {
            setAttendanceData(null);
          }
        } else {
          setAttendanceData(null);
        }
      })
      .catch(() => {
        setAttendanceData(null);
      })
      .finally(() => {
        setLoadingAttendance(false);
      });
  }, []);

  // ── Action handlers ────────────────────────────────────────────────────────
  const isApprovalDisabled = () => {
    if (actionTarget?.type === "approve") {
      const currentValue =
        approvedMinutes !== ""
          ? approvedMinutes
          : (actionTarget.request?.requested_minutes ??
            (actionTarget.request?.overtime_hours != null
              ? actionTarget.request.overtime_hours * 60
              : 0));
      const maxOtPerDay = overtimePolicy?.max_ot_per_day
        ? parseFloat(overtimePolicy.max_ot_per_day) * 60
        : 240;
      return currentValue > maxOtPerDay;
    }
    return false;
  };

  const handleConfirmAction = () => {
    if (!actionTarget) return;
    const { request, type } = actionTarget;
    const orgId = getLocalItem("organizationId");

    const payload = {
      organization_id: Number(orgId),
      status: type === "approve" ? "Approved" : "Rejected",
      manager_comments: type === "approve" ? actionRemarks.trim() : "",
      rejection_reason: type === "reject" ? actionRemarks.trim() : "",
    };

    if (type === "approve") {
      // Only send approved_minutes if user has edited the value
      if (approvedMinutes !== "" && approvedMinutes !== null) {
        const finalApprovedMins = Number(approvedMinutes);

        // Get max OT per day from policy, default to 4 hours (240 mins)

        payload.approved_minutes = finalApprovedMins;
      }
    }

    setActionLoader(true);
    approveOvertimeRequestApi(request.id, payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(type === "approve" ? "Overtime approved!" : "Overtime rejected.");
          fetchStaffSummaries();
          if (effectiveSelectedId) {
            fetchStaffRequests(effectiveSelectedId);
          }
          setActionTarget(null);
          setActionRemarks("");
          setApprovedMinutes("");
        } else {
          toast.error(resp?.message || "Something went wrong.");
        }
      })
      .catch((err) =>
        toast.error(err?.response?.data?.message || err?.message || "Something went wrong."),
      )
      .finally(() => setActionLoader(false));
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <style>{`
        .custom-search-input {
          padding-left: 36px !important;
        }
        .custom-datepicker-container .react-datepicker-wrapper {
          width: 130px !important;
          display: inline-block !important;
        }
        .custom-datepicker-container .react-datepicker__input-container {
          display: block !important;
        }
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
        @media (max-width: 991px) {
          .staff-detail-pane {
            display: none !important;
          }
          .staff-master-list {
            flex: 1 1 100% !important;
            max-width: 100% !important;
          }
        }
      `}</style>

      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            Overtime Requests
          </h1>
          <p style={{ margin: "3px 0 0", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            Review and manage overtime requests from staff
          </p>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div
        className="animate-slide-up stagger"
        style={{ display: "flex", gap: 12, flexWrap: "wrap" }}
      >
        <StatCard
          label="Pending"
          count={pendingCount}
          color="#f59e0b"
          bg="rgba(245,158,11,0.08)"
          border="rgba(245,158,11,0.2)"
          icon={AlertCircle}
        />
        <StatCard
          label="Approved"
          count={approvedCount}
          color="#10b981"
          bg="rgba(16,185,129,0.08)"
          border="rgba(16,185,129,0.2)"
          icon={CheckCircle}
        />
        <StatCard
          label="Rejected"
          count={rejectedCount}
          color="#f43f5e"
          bg="rgba(244,63,94,0.08)"
          border="rgba(244,63,94,0.2)"
          icon={XCircle}
        />
        <StatCard
          label="Total"
          count={totalCount}
          color="#6366f1"
          bg="rgba(99,102,241,0.08)"
          border="rgba(99,102,241,0.2)"
          icon={Clock}
        />
      </div>

      {/* ── Filters ── */}
      <div className="glass rounded-2xl animate-slide-up" style={{ padding: "16px 20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {/* Left section: Filter icon + pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--brand-indigo)",
                flexShrink: 0,
              }}
            >
              <Filter size={14} strokeWidth={2.5} />
            </div>

            {/* Status pills */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {STATUS_FILTERS.map((s) => {
                const active = statusFilter === s;
                const cfg = STATUS_CONFIG[s];
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      border: active
                        ? `1px solid ${cfg?.color ?? "#6366f1"}`
                        : "1px solid var(--glass-border)",
                      background: active ? (cfg ? cfg.bg : "rgba(99,102,241,0.12)") : "transparent",
                      color: active ? (cfg?.color ?? "#6366f1") : "var(--text-secondary)",
                    }}
                  >
                    {s}
                    {s === "Pending" && pendingCount > 0 && (
                      <span
                        style={{
                          marginLeft: 6,
                          padding: "1px 6px",
                          borderRadius: 999,
                          background: "#f43f5e",
                          color: "#fff",
                          fontSize: "0.65rem",
                          fontWeight: 800,
                        }}
                      >
                        {pendingCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right section: Date range + Search */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            {/* Date range */}
            <div
              className="custom-datepicker-container"
              style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap" }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "var(--icon-btn-bg)",
                  border: "1px solid var(--glass-border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-muted)",
                  flexShrink: 0,
                }}
              >
                <Calendar size={14} strokeWidth={2.5} />
              </div>
              <DatePicker
                selected={dateFrom}
                onChange={(d) => setDateFrom(d)}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                placeholderText="From date"
                dateFormat="dd MMM yyyy"
                className="form-control"
                isClearable
                popperProps={{ strategy: "fixed" }}
                portalId="datepicker-portal"
              />
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>—</span>
              <DatePicker
                selected={dateTo}
                onChange={(d) => setDateTo(d)}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom}
                placeholderText="To date"
                dateFormat="dd MMM yyyy"
                className="form-control"
                isClearable
                popperProps={{ strategy: "fixed" }}
                portalId="datepicker-portal"
              />
            </div>

            {/* Search */}
            <div style={{ width: "240px", minWidth: "180px", position: "relative" }}>
              <User
                size={14}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              />
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by staff name or ID…"
                className="form-control custom-search-input"
                style={{
                  width: "100%",
                  paddingLeft: 36,
                  paddingRight: 12,
                  paddingTop: 7,
                  paddingBottom: 7,
                  borderRadius: 9,
                  border: "1px solid var(--glass-border)",
                  background: "var(--icon-btn-bg)",
                  color: "var(--text-primary)",
                  fontSize: "0.8125rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                onBlur={(e) => (e.target.style.borderColor = "var(--glass-border)")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Request list ── */}
      {isLoadingStaff ? (
        <div
          className="glass rounded-2xl animate-fade-in"
          style={{ padding: "60px 24px", textAlign: "center", color: "var(--text-secondary)" }}
        >
          <span className="ds-spinner" role="status" aria-label="Loading" />
          <p style={{ marginTop: 12, fontSize: "0.875rem" }}>Loading overtime requests…</p>
        </div>
      ) : filteredStaff.length === 0 ? (
        <div
          className="glass rounded-2xl animate-fade-in"
          style={{ padding: "60px 24px", textAlign: "center", color: "var(--text-secondary)" }}
        >
          <Clock size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 600 }}>
            {searchQuery ? `No results for "${searchQuery}"` : "No overtime requests found"}
          </p>
          <p style={{ margin: "6px 0 0", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            {statusFilter !== "All"
              ? `No ${statusFilter.toLowerCase()} requests`
              : "Requests will appear here once staff submit them"}
          </p>
        </div>
      ) : (
        <div
          className="dashboard-grid"
          style={{
            display: "flex",
            gap: 20,
            minHeight: 500,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          {/* LEFT COLUMN: STAFF MASTER LIST (350px width on desktop) */}
          <div
            className="staff-master-list"
            style={{
              flex: "1 1 320px",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <p
              style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                margin: "0 0 4px",
              }}
            >
              Staff Members ({filteredStaff.length})
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredStaff.map((staff) => {
                const isSelected = effectiveSelectedId === staff.staff_id;
                const staffName = staff.employee_name;
                const staffId = staff.staff_id;
                const employeeCode = staff.employee_code;
                const designation = staff.designation;
                const initials =
                  staffName
                    .split(" ")
                    .map((w) => w[0] || "")
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) || "?";

                const pendingCount = staff.pending_requests_count;

                return (
                  <div
                    key={staffId}
                    onClick={() => {
                      setSelectedStaffId(staffId);
                      if (window.innerWidth < 992) {
                        setIsDetailDrawerOpen(true); // Open drawer only on mobile
                      }
                    }}
                    role="button"
                    style={{
                      padding: "14px 16px",
                      borderRadius: 16,
                      background: isSelected ? "rgba(99, 102, 241, 0.08)" : "var(--glass-bg)",
                      border: isSelected
                        ? "1.5px solid var(--brand-indigo)"
                        : "1px solid var(--glass-border)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.background = "rgba(99, 102, 241, 0.03)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "var(--glass-bg)";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      {/* Avatar */}
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: isSelected
                            ? "linear-gradient(135deg,#6366f1,#8b5cf6)"
                            : "rgba(99, 102, 241, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: isSelected ? "#fff" : "var(--brand-indigo)",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <span style={{ position: "absolute" }}>{initials}</span>
                        {staff.profile_image && (
                          <img
                            src={`${BaseStaffURL}${staff.profile_image}`}
                            alt={staffName}
                            style={{
                              position: "absolute",
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              zIndex: 1,
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div style={{ minWidth: 0 }}>
                        <p
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 700,
                            color: "var(--text-primary)",
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {staffName}
                        </p>
                        <p
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--text-muted)",
                            margin: "2px 0 0",
                          }}
                        >
                          {employeeCode || staffId} · {designation || "Staff"}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      {pendingCount > 0 && (
                        <BaseTooltip
                          label={`${pendingCount} Pending Request${pendingCount > 1 ? "s" : ""}`}
                          position="top"
                        >
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: 999,
                              background: "#f43f5e",
                              color: "#fff",
                              fontSize: "0.6rem",
                              fontWeight: 800,
                            }}
                          >
                            {pendingCount}
                          </span>
                        </BaseTooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT COLUMN: HISTORY & DETAIL PANEL (Hidden on screens < 992px via media query) */}
          <div
            className="staff-detail-pane"
            style={{
              flex: "1 1 500px",
              minWidth: "320px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {isLoadingRequests ? (
              <div
                className="glass rounded-2xl"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 24px",
                  color: "var(--text-secondary)",
                }}
              >
                <span className="ds-spinner" role="status" aria-label="Loading" />
                <p style={{ marginTop: 12, fontSize: "0.875rem" }}>Loading staff history…</p>
              </div>
            ) : activeStaff ? (
              <StaffOvertimeDashboard
                group={{
                  staff: {
                    name: activeStaff.employee_name,
                    staff_id: activeStaff.staff_id,
                    designation: activeStaff.designation,
                    profile_image: activeStaff.profile_image,
                  },
                  requests: staffRequests,
                }}
                onApprove={(r) => {
                  setActionTarget({ request: r, type: "approve" });
                  setActionRemarks("");
                  fetchAttendanceData(r.overtime_date ?? r.date, r.staff_id);
                }}
                onReject={(r) => {
                  setActionTarget({ request: r, type: "reject" });
                  setActionRemarks("");
                }}
                autoOt={autoOt}
                monthlyOvertimeHours={totalOvertimeHoursThisMonth}
                overtimePolicy={overtimePolicy}
              />
            ) : (
              <div
                className="glass rounded-2xl"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 24px",
                  color: "var(--text-muted)",
                }}
              >
                <User size={48} style={{ opacity: 0.15, marginBottom: 16 }} />
                <p style={{ margin: 0, fontSize: "0.9rem", fontWeight: 600 }}>
                  Select a Staff Member
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "0.75rem" }}>
                  Select a staff member from the left list to view their overtime logs and history.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Mobile Overtime History Drawer ── */}
      <BaseModal
        isOpen={isDetailDrawerOpen}
        toggler={() => setIsDetailDrawerOpen(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <User size={16} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
            <span>Overtime History & Dashboard</span>
          </div>
        }
        size="md"
        className="mobile-only-modal"
        hideSubmit={true}
      >
        {isLoadingRequests ? (
          <div style={{ textAlign: "center", padding: 40, color: "var(--text-secondary)" }}>
            <span className="ds-spinner" role="status" aria-label="Loading" />
            <p style={{ marginTop: 12, fontSize: "0.875rem" }}>Loading history…</p>
          </div>
        ) : activeStaff ? (
          <StaffOvertimeDashboard
            group={{
              staff: {
                name: activeStaff.employee_name,
                staff_id: activeStaff.staff_id,
                designation: activeStaff.designation,
                profile_image: activeStaff.profile_image,
              },
              requests: staffRequests,
            }}
            onApprove={(r) => {
              setActionTarget({ request: r, type: "approve" });
              setActionRemarks("");
              setIsDetailDrawerOpen(false);
              fetchAttendanceData(r.overtime_date ?? r.date, r.staff_id);
            }}
            onReject={(r) => {
              setActionTarget({ request: r, type: "reject" });
              setActionRemarks("");
              setIsDetailDrawerOpen(false);
            }}
            autoOt={autoOt}
            monthlyOvertimeHours={totalOvertimeHoursThisMonth}
            overtimePolicy={overtimePolicy}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
            No staff selected
          </div>
        )}
      </BaseModal>

      {/* ── Approve / Reject modal ── */}
      <BaseModal
        isOpen={!!actionTarget}
        toggler={() => {
          setActionTarget(null);
          setActionRemarks("");
          setApprovedMinutes("");
          setAttendanceData(null);
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  actionTarget?.type === "approve"
                    ? "rgba(16,185,129,0.12)"
                    : "rgba(244,63,94,0.12)",
              }}
            >
              {actionTarget?.type === "approve" ? (
                <CheckCircle size={15} strokeWidth={2.5} style={{ color: "#10b981" }} />
              ) : (
                <XCircle size={15} strokeWidth={2.5} style={{ color: "#f43f5e" }} />
              )}
            </div>
            <span>{actionTarget?.type === "approve" ? "Approve Overtime" : "Reject Overtime"}</span>
          </div>
        }
        submit={handleConfirmAction}
        submitText={actionTarget?.type === "approve" ? "Approve" : "Reject"}
        disabled={actionLoader || isApprovalDisabled()}
        loader={actionLoader}
        size="md"
      >
        {actionTarget && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Request summary - Blue card */}
            <div
              style={{
                padding: "16px 18px",
                borderRadius: 12,
                background:
                  "linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.08))",
                border: "1px solid rgba(99, 102, 241, 0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                  >
                    {(actionTarget.request?.staff?.name ?? activeStaff?.employee_name ?? "Staff")
                      .split(" ")
                      .map((w) => w[0] || "")
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) || "??"}
                  </div>
                  <div>
                    <span
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "var(--text-primary)",
                        display: "block",
                      }}
                    >
                      {actionTarget.request?.staff?.name ?? activeStaff?.employee_name ?? "Staff"}
                    </span>
                    <p
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--text-muted)",
                        margin: "2px 0 0",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Calendar size={11} strokeWidth={2.5} />
                      {fmtDate(actionTarget.request?.overtime_date ?? actionTarget.request?.date)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                padding: "14px 16px",
                borderRadius: 10,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              {/* Project Name */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                }}
              >
                <span
                  className="text-secondary fw-semibold text-uppercase d-inline-block"
                  style={{ minWidth: 130, fontSize: "0.7rem", letterSpacing: "0.06em" }}
                >
                  Project Name:
                </span>

                <span className="small fw-semibold text-break text-body">
                  {actionTarget.request?.project_task || "—"}
                </span>
              </div>

              {/* Reason */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                }}
              >
                <span
                  className="text-secondary fw-semibold text-uppercase d-inline-block"
                  style={{ minWidth: 130, fontSize: "0.7rem", letterSpacing: "0.06em" }}
                >
                  Reason:
                </span>

                <span className="small fw-medium text-break text-body">
                  {actionTarget.request?.reason || "—"}
                </span>
              </div>

              {/* Attendance Data */}
              {actionTarget?.type === "approve" && !loadingAttendance && attendanceData && (
                <>
                  <div
                    className="mt-3 pt-2 text-secondary fw-semibold text-uppercase mb-2"
                    style={{
                      borderTop: "1px solid var(--glass-border)",
                      fontSize: "0.7rem",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Attendance Details
                  </div>

                  <div className="d-flex align-items-baseline gap-2">
                    <span
                      className="text-secondary fw-medium d-inline-block"
                      style={{ minWidth: 130, fontSize: "0.8rem" }}
                    >
                      Total worked
                    </span>

                    <span className="small fw-semibold text-body">
                      {formatTimeForDisplay(attendanceData.total_working_hours)}
                    </span>
                  </div>

                  <div className="d-flex align-items-baseline gap-2">
                    <span
                      className="text-secondary fw-medium d-inline-block"
                      style={{ minWidth: 130, fontSize: "0.8rem" }}
                    >
                      Applicable OT
                    </span>

                    <span className="small fw-semibold text-body">
                      {formatTimeForDisplay(attendanceData.applicable_over_time)}
                    </span>
                  </div>

                  {attendanceData.overtime_calculation_type === "After Threshold" &&
                    attendanceData.minimum_ot_threshold && (
                      <div className="d-flex align-items-baseline gap-2">
                        <span
                          className="text-secondary fw-medium d-inline-block"
                          style={{ minWidth: 130, fontSize: "0.8rem" }}
                        >
                          Minimum threshold
                        </span>

                        <span className="small fw-semibold text-body">
                          {formatTimeForDisplay(attendanceData.minimum_ot_threshold)}
                        </span>
                      </div>
                    )}
                </>
              )}

              {actionTarget?.type === "approve" && loadingAttendance && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    fontStyle: "italic",
                  }}
                >
                  Loading attendance data...
                </div>
              )}

              {/* Requested and Actual Time */}
              {actionTarget?.type === "approve" &&
                (() => {
                  const originalMins = actionTarget.request
                    ? (actionTarget.request.requested_minutes ??
                      (actionTarget.request.overtime_hours != null
                        ? actionTarget.request.overtime_hours * 60
                        : 0))
                    : 0;

                  const currentValue = approvedMinutes !== "" ? approvedMinutes : originalMins;

                  const maxOtPerDay = overtimePolicy?.max_ot_per_day
                    ? parseFloat(overtimePolicy.max_ot_per_day) * 60
                    : 240;

                  const isOverLimit = currentValue > maxOtPerDay;

                  return (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 40,
                          paddingTop: 10,
                          marginTop: 2,
                          borderTop: "1px solid var(--glass-border)",
                        }}
                      >
                        {/* Requested OT */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.7rem",
                              color: "var(--text-muted)",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                            }}
                          >
                            Requested OT:
                          </span>

                          <span
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: 700,
                              color: "#6366f1",
                            }}
                          >
                            {fmtHours(originalMins)}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.7rem",
                              color: "var(--text-muted)",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                            }}
                          >
                            Approve OT:
                          </span>

                          <input
                            type="number"
                            min="0"
                            max={maxOtPerDay}
                            value={currentValue}
                            onChange={(e) => {
                              const raw = e.target.value;

                              if (raw === "") {
                                setApprovedMinutes("");
                                return;
                              }

                              const val = parseInt(raw) || 0;
                              setApprovedMinutes(Math.max(0, val));
                            }}
                            style={{
                              width: 100,
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: isOverLimit
                                ? "1px solid #f43f5e"
                                : "1px solid rgba(99, 102, 241, 0.3)",
                              background: isOverLimit
                                ? "rgba(244, 63, 94, 0.08)"
                                : "rgba(99, 102, 241, 0.08)",
                              color: "var(--text-primary)",
                              fontSize: "0.875rem",
                              fontWeight: 700,
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>

                      {/* Warning Message */}
                      {isOverLimit && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            paddingTop: 6,
                          }}
                        >
                          <AlertCircle size={12} strokeWidth={2.5} style={{ color: "#f43f5e" }} />
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: "#f43f5e",
                              fontWeight: 600,
                            }}
                          >
                            Maximum {maxOtPerDay / 60} hours can be approved according to OT policy.
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>

            {/* Policy Information - Only for Pre Overtime */}
            {actionTarget.request?.request_type === "Pre Overtime Required" && (
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "rgba(249,115,22,0.06)",
                  border: "1px solid rgba(249,115,22,0.15)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#f97316" }}>
                    Active Policy: {overtimePolicy?.policy_name || "—"}
                  </span>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 12,
                      background: "rgba(249,115,22,0.12)",
                      color: "#f97316",
                    }}
                  >
                    {overtimePolicy?.approval_type || "—"}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    fontSize: "0.75rem",
                  }}
                >
                  <div>
                    <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
                      Calculation Type:
                    </span>{" "}
                    <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {overtimePolicy?.calculation_type || "—"}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>Threshold:</span>{" "}
                    <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {overtimePolicy?.minimum_ot_threshold
                        ? `${overtimePolicy.minimum_ot_threshold}h`
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Remarks */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  marginBottom: 6,
                }}
              >
                Remarks{" "}
                <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                rows={3}
                placeholder={
                  actionTarget?.type === "approve"
                    ? "Add any notes for this approval…"
                    : "Provide a reason for rejection…"
                }
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 10,
                  border: "1px solid var(--glass-border)",
                  background: "var(--icon-btn-bg)",
                  color: "var(--text-primary)",
                  fontSize: "0.875rem",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                onBlur={(e) => (e.target.style.borderColor = "var(--glass-border)")}
              />

              {/* Policy Note */}
              {actionTarget?.type === "approve" &&
                (() => {
                  const maxOtPerDay = overtimePolicy?.max_ot_per_day
                    ? parseFloat(overtimePolicy.max_ot_per_day)
                    : 4;

                  return (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "6px 10px",
                        borderRadius: 6,
                        background: "rgba(99, 102, 241, 0.05)",
                        border: "1px solid rgba(99, 102, 241, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <AlertCircle size={11} strokeWidth={2.5} style={{ color: "#6366f1" }} />
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: "var(--text-secondary)",
                          fontWeight: 500,
                          lineHeight: "1.3",
                        }}
                      >
                        Note: As per the OT policy, a maximum of {maxOtPerDay} hours can be
                        approved.
                      </span>
                    </div>
                  );
                })()}
            </div>
          </div>
        )}
      </BaseModal>
    </div>
  );
};

export default OvertimeRequests;
